import React, { useEffect, useState } from "react";
import { Check, X, MessageCircleQuestion } from "lucide-react";
import { PageBody, PageHeader } from "@/components/AppLayout";
import { approveExpense, listExpenses, rejectExpense, requestInfo } from "@/lib/api";
import StatusPill from "@/components/StatusPill";
import { useToast } from "@/hooks/use-toast";
import { APPROVAL, EXPENSE } from "@/constants/testIds";

function money(n, c = "USD") { return `${c === "USD" ? "$" : c + " "}${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

export default function Approvals() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [action, setAction] = useState(null); // "approve" | "reject" | "info"
  const [note, setNote] = useState("");
  const [working, setWorking] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [pending, infoReq] = await Promise.all([
      listExpenses({ status: "pending" }),
      listExpenses({ status: "info_requested" }),
    ]);
    setItems([...pending, ...infoReq]);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const apply = async () => {
    if (!selected || !action) return;
    setWorking(true);
    try {
      if (action === "approve") await approveExpense(selected.id, note || null);
      if (action === "reject") {
        if (!note.trim()) throw new Error("Reason is required");
        await rejectExpense(selected.id, note);
      }
      if (action === "info") {
        if (!note.trim()) throw new Error("Question is required");
        await requestInfo(selected.id, note);
      }
      toast({ title: `Expense ${action === "approve" ? "approved" : action === "reject" ? "rejected" : "info requested"}` });
      setSelected(null);
      setAction(null);
      setNote("");
      await load();
    } catch (err) {
      toast({ title: "Could not complete", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    } finally {
      setWorking(false);
    }
  };

  return (
    <>
      <PageHeader title="Approvals" subtitle="Receipts waiting for your steward's eye." />
      <PageBody>
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            <div className="border border-border bg-card rounded-md overflow-hidden">
              <div className="px-6 py-3 bg-accent/40 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                {items.length} pending
              </div>
              {loading ? (
                <div className="px-6 py-12 text-center text-sm text-muted-foreground">Loading…</div>
              ) : items.length === 0 ? (
                <div className="px-6 py-16 text-center">
                  <div className="serif text-xl text-primary">All clear</div>
                  <p className="text-sm text-muted-foreground mt-1">No expenses are awaiting approval.</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {items.map((e) => (
                    <button
                      key={e.id}
                      onClick={() => { setSelected(e); setAction(null); setNote(""); }}
                      data-testid={EXPENSE.row(e.id)}
                      className={`w-full text-left px-6 py-4 hover:bg-accent/30 transition-colors ${selected?.id === e.id ? "bg-accent/50" : ""}`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{e.vendor}</div>
                          <div className="text-xs text-muted-foreground mt-0.5 truncate">{e.program_name} · {e.category} · by {e.submitted_by_name}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="mono text-sm text-primary tabular">{money(e.amount, e.currency)}</div>
                          <div className="text-[11px] text-muted-foreground mono mt-0.5">{e.spent_on}</div>
                        </div>
                      </div>
                      <div className="mt-2"><StatusPill status={e.status} /></div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-5">
            {!selected ? (
              <div className="border border-dashed border-border rounded-md p-8 text-center text-sm text-muted-foreground">
                Select an expense to review and decide.
              </div>
            ) : (
              <div className="border border-border bg-card rounded-md overflow-hidden">
                {selected.receipt_data_url ? (
                  <img src={selected.receipt_data_url} alt="Receipt" className="w-full max-h-[280px] object-contain bg-muted" />
                ) : (
                  <div className="h-32 flex items-center justify-center bg-muted text-xs text-muted-foreground">No receipt image</div>
                )}
                <div className="p-6 space-y-4">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Vendor</div>
                    <div className="serif text-xl mt-1 text-primary">{selected.vendor}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <KV label="Amount" value={<span className="mono tabular text-primary">{money(selected.amount, selected.currency)}</span>} />
                    <KV label="Date" value={<span className="mono">{selected.spent_on}</span>} />
                    <KV label="Program" value={selected.program_name} />
                    <KV label="Category" value={selected.category} />
                  </div>
                  {selected.notes && (
                    <div className="border-t border-border pt-4">
                      <div className="text-xs uppercase tracking-wider text-muted-foreground">Volunteer note</div>
                      <p className="text-sm mt-1">{selected.notes}</p>
                    </div>
                  )}

                  <div className="border-t border-border pt-4 space-y-3">
                    <div className="flex gap-2">
                      <ActionBtn data-testid={APPROVAL.approveBtn(selected.id)} active={action === "approve"} onClick={() => setAction("approve")} icon={Check} label="Approve" tone="approve" />
                      <ActionBtn data-testid={APPROVAL.infoBtn(selected.id)} active={action === "info"} onClick={() => setAction("info")} icon={MessageCircleQuestion} label="More info" tone="info" />
                      <ActionBtn data-testid={APPROVAL.rejectBtn(selected.id)} active={action === "reject"} onClick={() => setAction("reject")} icon={X} label="Reject" tone="reject" />
                    </div>
                    {action && (
                      <div className="space-y-2">
                        <textarea
                          data-testid={APPROVAL.decisionNote}
                          rows={3}
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          placeholder={action === "approve" ? "Optional note for the volunteer" : action === "reject" ? "Reason for rejection (required)" : "What more do you need? (required)"}
                          className="w-full td-input"
                        />
                        <button
                          data-testid={APPROVAL.confirm}
                          onClick={apply}
                          disabled={working}
                          className="w-full rounded-md bg-primary text-primary-foreground px-4 py-2.5 text-sm font-medium hover:bg-primary/90 disabled:opacity-60"
                        >
                          {working ? "Working…" : `Confirm ${action}`}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </PageBody>
      <style>{`
        .td-input { width: 100%; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); border-radius: 6px; padding: 10px 12px; font-size: 14px; color: hsl(var(--foreground)); }
        .td-input:focus { outline: none; border-color: hsl(var(--ring)); box-shadow: 0 0 0 3px hsl(var(--ring) / 0.12); }
      `}</style>
    </>
  );
}

function ActionBtn({ active, onClick, icon: Icon, label, tone, ...rest }) {
  const tones = {
    approve: { active: "bg-status-approved text-white", inactive: "border border-border hover:bg-accent" },
    reject: { active: "bg-destructive text-destructive-foreground", inactive: "border border-border hover:bg-accent" },
    info: { active: "bg-primary text-primary-foreground", inactive: "border border-border hover:bg-accent" },
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      {...rest}
      className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm transition-colors ${active ? tones.active : tones.inactive}`}
    >
      <Icon size={14} /> {label}
    </button>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm mt-1">{value}</div>
    </div>
  );
}
